/**
 * Claim detail (`/sinistres/:idSin` — fiches 22807 / 22740 / 22742 / 22744 /
 * 22746).
 *
 * - `WS2_EA_FIC_SIN` and the four claim tables are fetched eagerly and in
 *   parallel (`useClaimDetail`), each card showing its own loading / error /
 *   empty state.
 * - Header: title `NOM_ASSURE` (else the list row's insured carried in the
 *   router state), subtitle `LIB_PROD · NCBT_SIN`, breadcrumb Sinistres ›
 *   NCBT_SIN, status `Badge` (`LIB_MOT`, coloured by `ETAT`) in the actions
 *   slot. « Nous contacter » is not specified — not rendered.
 * - `Tabs` with stable values `details`, `intervenants`, `charges`,
 *   `documents`, `pieces` (+ the count of pieces « EN ATTENTE »).
 * - Rule context: base variables + `claim_id`, `claim_status`, `claim_type`,
 *   `claim_product`, `claim_responsibility`.
 */

import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Badge } from '@components/base/display';
import { Tabs } from '@components/base/navigation';
import { RuleContextProvider } from '@components/shared/configurable';

import type { ClaimChargeRow, ClaimStakeholderRow } from '../api/claimsService';
import { useClaimDetailRuleContext } from '../components/claims/claimContext';
import { buildClaimDetailHeader, countPendingPieces } from '../components/claims/claimDetailHeader';
import { buildClaimDetailsValues } from '../components/claims/claimDetailsConfig';
import { readClaimRouteState } from '../components/claims/claimRouteState';
import {
  CLAIM_DETAIL_TABS,
  pendingPiecesTabBadgeLabel,
  type ClaimDetailTabKey,
} from '../components/claims/claimsText';
import {
  ClaimChargesCardConfigurable as ClaimChargesCard,
  ClaimDetailsCardConfigurable as ClaimDetailsCard,
  ClaimDocumentsCardConfigurable as ClaimDocumentsCard,
  ClaimPiecesCardConfigurable as ClaimPiecesCard,
  ClaimStakeholdersCardConfigurable as ClaimStakeholdersCard,
} from '../components/claims/register';
import { queryCardState, useClaimDetail } from '../components/claims/useClaimQueries';
import { PageHeader } from '../components/PageHeader';
import type { DocumentItem } from '../components/shared/documentItems';
import type { PieceItem } from '../components/shared/pieceItems';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const NO_STAKEHOLDERS: readonly ClaimStakeholderRow[] = [];
const NO_CHARGES: readonly ClaimChargeRow[] = [];
const NO_DOCUMENTS: readonly DocumentItem[] = [];
const NO_PIECES: readonly PieceItem[] = [];

const DEFAULT_TAB: ClaimDetailTabKey = 'details';

export function ClaimDetailPage() {
  const { tenantSlug, idSin = '' } = useParams<{ tenantSlug: string; idSin: string }>();
  const location = useLocation();
  const text = EXTRANET_PAGE_TEXT.claim;
  const listPath = extranetPath(tenantSlug, 'sinistres');

  const { record, stakeholders, charges, documents, pieces } = useClaimDetail(idSin);
  const ruleContext = useClaimDetailRuleContext(idSin, record.data);

  const routeState = useMemo(() => readClaimRouteState(location.state), [location.state]);
  const header = buildClaimDetailHeader(record.data, routeState, idSin, text.title);
  const detailsValues = useMemo(() => buildClaimDetailsValues(record.data ?? null), [record.data]);
  const pieceItems = pieces.data ?? NO_PIECES;
  const pendingCount = useMemo(() => countPendingPieces(pieceItems), [pieceItems]);

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-claim">
        <PageHeader
          title={header.title}
          subtitle={header.subtitle}
          backTo={listPath}
          breadcrumbs={[
            { key: 'sinistres', label: text.breadcrumb, to: listPath },
            { key: 'sinistre', label: header.crumb },
          ]}
          actions={
            header.status ? (
              <Badge variant={header.status.variant} data-testid="claim-status-badge">
                {header.status.label}
              </Badge>
            ) : undefined
          }
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Tabs defaultValue={DEFAULT_TAB}>
            <Tabs.List className="overflow-x-auto">
              {CLAIM_DETAIL_TABS.map((tab) => (
                <Tabs.Tab
                  key={tab.key}
                  value={tab.key}
                  className="shrink-0 whitespace-nowrap"
                  data-tab-key={tab.key}
                >
                  {tab.label}
                  {tab.key === 'pieces' && pendingCount > 0 && (
                    <Badge
                      variant="warning"
                      size="sm"
                      pill
                      className="ml-1.5"
                      aria-label={pendingPiecesTabBadgeLabel(pendingCount)}
                      data-testid="claim-pending-pieces-count"
                    >
                      {pendingCount}
                    </Badge>
                  )}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            <Tabs.Panel value="details">
              <ClaimDetailsCard values={detailsValues} {...queryCardState(record)} />
            </Tabs.Panel>
            <Tabs.Panel value="intervenants">
              <ClaimStakeholdersCard
                rows={stakeholders.data ?? NO_STAKEHOLDERS}
                {...queryCardState(stakeholders)}
              />
            </Tabs.Panel>
            <Tabs.Panel value="charges">
              <ClaimChargesCard rows={charges.data ?? NO_CHARGES} {...queryCardState(charges)} />
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <ClaimDocumentsCard
                documents={documents.data ?? NO_DOCUMENTS}
                {...queryCardState(documents)}
              />
            </Tabs.Panel>
            <Tabs.Panel value="pieces">
              <ClaimPiecesCard pieces={pieceItems} {...queryCardState(pieces)} />
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </RuleContextProvider>
  );
}
